import { GetterTree, MutationTree, ActionTree } from "vuex/types/index";
import { Snapshot, External } from "@foxone/api/src/models/wallet/mixin";
import utils from "@/utils";

export const state = () => ({
  assetId: "",
  snapshots: [],
  externals: [],
  hasNext: true
});

export type SnapshotsState = ReturnType<
  () => {
    assetId: string;
    snapshots: Snapshot[];
    externals: External[];
    hasNext: boolean;
  }
>;

const getters: GetterTree<SnapshotsState, {}> = {
  getSnapshots(state) {
    return state.snapshots;
  },
  getGroupedSnapshots(state) {
    const externals = state.externals.map(e => ({
      ...e,
      display: "external"
    }));
    const all = [...state.snapshots, ...externals].sort((a, b) => {
      const t1 = new Date(a.created_at).getTime();
      const t2 = new Date(b.created_at).getTime();
      return t2 - t1;
    });
    const data = all.reduce((group, snapshot) => {
      const date = utils.date.formatDate(snapshot.created_at, "YYYY/MM/DD");
      if (!group[date]) {
        group[date] = [];
      }
      group[date].push(snapshot);
      return group;
    }, {});
    return data;
  }
};

const mutations: MutationTree<SnapshotsState> = {
  SET_ASSET_ID(state, value) {
    state.assetId = value;
  },
  SET_EXTERNALS(state, externals) {
    const lasts = state.externals.slice(-10);
    const filted = externals.filter(
      s => lasts.findIndex(l => l.transaction_id === s.transaction_id) < 0
    );
    state.externals = state.externals.concat(filted);
  },
  SET_SNAPSHOTS(state, snapshots) {
    const lasts = state.snapshots.slice(-10);
    const filted = snapshots.filter(
      s => lasts.findIndex(l => l.snapshot_id === s.snapshot_id) < 0
    );
    state.snapshots = state.snapshots.concat(filted);
    if (snapshots.length < 10) {
      state.hasNext = false;
    } else {
      state.hasNext = true;
    }
  },
  REMOVE_EXTERNALS(state) {
    state.externals = [];
  },
  REMOVE_SNAPSHOTS(state) {
    state.snapshots = [];
    state.hasNext = true;
  }
};

const actions: ActionTree<SnapshotsState, {}> = {
  async loadSnapshots({ commit, state }, assetId) {
    const { snapshots: oldSnapshots } = state;
    const last = oldSnapshots[oldSnapshots.length - 1];
    const offset = last && last.created_at;
    const params = { asset: assetId, limit: 10, offset };
    const snapshots = await this.$api.wallet.mixin.getSnapshots(params);
    commit("SET_SNAPSHOTS", snapshots);
  },
  async loadExternals({ state, commit }, params) {
    const { externals: oldExternals } = state;
    const last = oldExternals[oldExternals.length - 1];
    const offset = last && last.created_at;
    let hasNext = true;
    do {
      const externals = await this.$api.wallet.mixin.getExternalTransactions({
        ...params,
        limit: 500,
        offset
      });
      if (externals.length < 500) {
        hasNext = false;
      }
      commit("SET_EXTERNALS", externals);
    } while (hasNext);
  },
  removeSnapshots({ commit }) {
    commit("REMOVE_SNAPSHOTS");
    commit("REMOVE_EXTERNALS");
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
